import {
  Add,
  CheckCircleOutline,
  MoreVert,
  NotInterested
} from '@mui/icons-material';
import {
  Avatar,
  Button,
  Grid,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  SvgIcon,
  Tab,
  Tabs,
  Tooltip,
  Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import {
  colorLightYellow,
  colorPrimary,
  colorTextBlack,
  colorTextGrey
} from '../../config/theme';
import CustomDataTable from '../../components/CustomDataTable';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import {
  activateDeactivateRubrics,
  deleteRubrics,
  getRubrics,
  getRubricsCount,
  revertRubricPageData
} from '../../redux/reducers/rubricSlice';
import { Container } from '../../utils/styledLayout';
import { useNavigate } from 'react-router-dom';
import SnackBarBox from '../../components/SnackBarBox';
import { createTitle, handleError, isArrayNotEmpty } from '../../utils/utils';
import ConfirmBox from '../../components/ConfirmBox';
import { setActiveModule } from '../../redux/reducers/layoutSlice';
import { TrashIcon } from '../../utils/iconSvg';
import CustomTabLabel from '../../custom/CustomTabLabel';

function RubricPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [initialValues, setInitialValues] = useState({
    tabValue: 0,
    page: 0,
    size: 10
  });
  const { authData } = useSelector(state => state.auth);
  const [selectedRubrics, setSelectedRubrics] = useState([]);
  const [showError, setShowError] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [confirmation, setConfimation] = useState(null);
  const open = Boolean(anchorEl);

  const {
    isRubricsLoading,
    isRubricsSuccess,
    isRubricsFailed,
    isActivateDeactivateRubricsLoading,
    isActivateDeactivateRubricsSuccess,
    isActivateDeactivateRubricsFailed,
    activateDeactivateRubricsResponse,
    isDeleteRubricsLoading,
    isDeleteRubricsSuccess,
    isDeleteRubricsFailed,
    rubricsData,
    rubricsErrorContainer,
    deleteRubricsErrorContainer,
    activateDeactivateRubricsErrorContainer
  } = useSelector(state => state.rubric);

  useEffect(() => {
    document.title = createTitle('Rubrics');
    dispatch(setActiveModule('Evaluation Rubrics'));
    dispatch(getRubricsCount());
    return () => {
      dispatch(revertRubricPageData());
    };
  }, []);

  useEffect(() => {
    dispatch(
      getRubrics({
        type:
          initialValues.tabValue === 1
            ? true
            : initialValues.tabValue === 2
              ? false
              : 'all',
        page: initialValues.page,
        size: initialValues.size
      })
    );
  }, [initialValues]);

  const columns = [
    {
      uinqueKey: 'rubricId',
      id: 'rubricName',
      label: 'Rubric Name',
      sortable: true,
      minWidth: '100px',
      render: row => {
        return (
          <Typography variant="body2" fontWeight={'600'}>
            {row.rubricName}
          </Typography>
        );
      }
    },
    {
      id: 'createdBy',
      label: 'Created By'
    },
    {
      id: 'createdDate',
      label: 'Created On',
      minWidth: '150px',
      render: row => {
        return (
          <>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Typography fontSize={'14px'} fontWeight={'600'}>
                {row.createdDate
                  ? moment(row.createdDate).format('DD-MM-YYYY hh:mm A')
                  : 'NA'}
              </Typography>
            </div>
          </>
        );
      }
    },
    // {
    //   id: 'users',
    //   label: 'Group Users'
    // },
    {
      id: 'status',
      label: 'Status',
      width: '150px',
      render: row => (
        <>
          <div
            style={{
              fontWeight: '600',
              fontSize: 12,
              backgroundColor: row.status ? '#EBF9D9' : '#FDF0E3',
              padding: '4px 8px 4px 8px',
              borderRadius: '8px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              color: row.status ? '#749E35' : '#D62828',
              width: row.status ? '62px' : '70px',
              height: '22px'
            }}
          >
            <Avatar
              sx={{
                width: 8,
                height: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                backgroundColor: row.status ? '#749E35' : '#D62828',
                color: 'white',
                fontSize: '12px',
                marginRight: '5px'
              }}
            >
              <></>
            </Avatar>
            {row.status ? 'Active' : 'Inactive'}
          </div>
        </>
      )
    }
  ];

  const handleMenuActions = action => {
    setShowError(null);
    if (action === 'Delete') {
      handleDeleteRubrics();
    } else if (action === 'De-Activate') {
      handleDeActivateRubrics();
    } else if (action === 'Activate') {
      handleActivateRubrics();
    }
  };

  const handleDeleteRubrics = () => {
    let errorMessage = '';
    if (isArrayNotEmpty(rubricsData?.rubrics)) {
      if (isArrayNotEmpty(selectedRubrics)) {
        setConfimation(true);
      } else {
        errorMessage = 'Please select rubric[s] to delete.';
      }
    }
    setShowError(errorMessage);
  };

  const handleActivateRubrics = () => {
    let errorMessage = '';
    if (isArrayNotEmpty(rubricsData?.rubrics)) {
      if (!isArrayNotEmpty(selectedRubrics)) {
        errorMessage = 'Please select rubric[s] to activate.';
      } else {
        const selectedUsersData = rubricsData?.rubrics.filter(user =>
          selectedRubrics.includes(user.rubricId)
        );
        const allUsersActive = selectedUsersData.every(user => !user.status);
        if (!allUsersActive) {
          errorMessage = 'Please select only inactive rubric[s] to activate.';
        } else {
          let payload = {
            isActivate: true,
            ids: [...selectedRubrics],
            loggedInUserName: authData?.userName
          };
          dispatch(activateDeactivateRubrics(payload));
        }
      }
    }
    setShowError(errorMessage);
  };

  const handleDeActivateRubrics = () => {
    let errorMessage = '';
    if (isArrayNotEmpty(rubricsData?.rubrics)) {
      if (!isArrayNotEmpty(selectedRubrics)) {
        errorMessage = 'Please select rubric[s] to in-activate.';
      } else {
        const selectedUsersData = rubricsData?.rubrics.filter(user =>
          selectedRubrics.includes(user.rubricId)
        );
        const allUsersActive = selectedUsersData.every(user => user.status);
        if (!allUsersActive) {
          errorMessage = 'Please select only active rubric[s] to in-activate.';
        } else {
          let payload = {
            isActivate: false,
            ids: [...selectedRubrics],
            loggedInUserName: authData?.userName
          };

          dispatch(activateDeactivateRubrics(payload));
        }
      }
    }
    setShowError(errorMessage);
  };

  const deleteUser = () => {
    let deletePayload = {
      ids: [...selectedRubrics],
      loggedInUserName: authData?.userName
    };
    dispatch(deleteRubrics(deletePayload));
  };

  const handleReset = () => {
    setSelectedRubrics([]);
    setShowError(null);
    setConfimation(null);
    setInitialValues({ tabValue: 0, page: 0, size: 10, searchFilter: '' });
    setAnchorEl(null);
    dispatch(getRubricsCount());
  };

  useEffect(() => {
    if (isActivateDeactivateRubricsSuccess || isDeleteRubricsSuccess) {
      return handleReset();
    }
  }, [isActivateDeactivateRubricsSuccess, isDeleteRubricsSuccess]);

  const handleOnRowClick = row => {
    return navigate(`rubric-details`, {
      state: {
        rubricId: row.rubricId
      }
    });
  };

  return (
    <>
      <Container mb={1}>
        <Grid>
          <Typography fontSize={32} fontWeight="700" display={'flex'}>
            Evaluation Rubrics{' '}
            {/* <Typography
              ml={1}
              fontSize={20}
              fontWeight="700"
              alignSelf={'center'}
            >
              ({rubricsData?.totalCount})
            </Typography> */}
          </Typography>
        </Grid>
        <Grid>
          <Button
            onClick={() => {
              navigate('new-rubric');
            }}
            variant="contained"
            sx={{
              fontWeight: '700',
              boxShadow: 'none'
            }}
          >
            <SvgIcon
              fontSize="small"
              sx={{ marginRight: 0.6, height: '16px', width: '16px' }}
            >
              <svg
                width="17"
                height="16"
                viewBox="0 0 17 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8.49996 2.66669V13.3334M3.16663 8.00002H13.8333"
                  stroke="#1F2933"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </SvgIcon>{' '}
            Add rubric
          </Button>
        </Grid>
      </Container>
      <Grid
        container
        display={'flex'}
        flexDirection={'column'}
        elevation={0}
        component={Paper}
        borderRadius={'8px'}
        sx={{ paddingX: '14px', pt: 1 }}
      >
        <Container sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={initialValues.tabValue}
            onChange={(val, newValue) => {
              setInitialValues({
                ...initialValues,
                tabValue: newValue,
                page: 0
              });
              setSelectedRubrics([]);
            }}
            TabIndicatorProps={{
              sx: {
                height: '4px',
                borderRadius: '4px'
              }
            }}
          >
            <Tab
              sx={{
                fontSize: '16px',
                minHeight: 44,
                height: 44,
                color: colorTextGrey,
                fontWeight: initialValues.tabValue === 0 ? '700' : '600',
                '&.Mui-selected': {
                  color:
                    initialValues.tabValue === 0
                      ? colorTextBlack
                      : colorTextGrey
                }
              }}
              // label="All Rubrics"
              label={
                <CustomTabLabel
                  title="All Rubrics"
                  count={
                    rubricsData?.allCount < 10
                      ? `0${rubricsData?.allCount}`
                      : rubricsData?.allCount
                  }
                  selected={initialValues.tabValue === 0}
                />
              }
            />
            <Tab
              sx={{
                fontSize: '16px',
                minHeight: 44,
                height: 44,
                color: colorTextGrey,
                fontWeight: initialValues.tabValue === 1 ? '700' : '600',
                '&.Mui-selected': {
                  color:
                    initialValues.tabValue === 1
                      ? colorTextBlack
                      : colorTextGrey
                }
              }}
              // label="Active Rubrics"
              label={
                <CustomTabLabel
                  title="Active Rubrics"
                  count={
                    rubricsData?.activeCount < 10
                      ? `0${rubricsData?.activeCount}`
                      : rubricsData?.activeCount
                  }
                  selected={initialValues.tabValue === 1}
                />
              }
            />
            <Tab
              sx={{
                fontSize: '16px',
                minHeight: 44,
                height: 44,
                color: colorTextGrey,
                fontWeight: initialValues.tabValue === 2 ? '700' : '600',
                '&.Mui-selected': {
                  color:
                    initialValues.tabValue === 2
                      ? colorTextBlack
                      : colorTextGrey
                }
              }}
              //  label="Inactive Rubrics"
              label={
                <CustomTabLabel
                  title="Inactive Rubrics"
                  count={
                    rubricsData?.inActiveCount < 10
                      ? `0${rubricsData?.inActiveCount}`
                      : rubricsData?.inActiveCount
                  }
                  selected={initialValues.tabValue === 2}
                />
              }
            />
          </Tabs>
          <div style={{ display: 'flex', margin: 'auto 10px' }}>
            {/* <SearchInput
              onSearchChange={handleSearchChange}
              searchTerm={searchTerm}
            /> */}
            <Tooltip title="More options">
              <IconButton
                aria-label="more"
                id="long-button"
                aria-controls={open ? 'long-menu' : undefined}
                aria-expanded={open ? 'true' : undefined}
                aria-haspopup="true"
                onClick={event => setAnchorEl(event.currentTarget)}
              >
                <MoreVert />
              </IconButton>
            </Tooltip>

            <Menu
              id="long-menu"
              MenuListProps={{
                'aria-labelledby': 'long-button'
              }}
              anchorEl={anchorEl}
              open={open}
              onClose={() => {
                setAnchorEl(null);
                setShowError(null);
              }}
              PaperProps={{
                elevation: 0,
                sx: {
                  width: '20ch',
                  overflow: 'visible',
                  filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                  mt: 1.5,
                  '& .MuiAvatar-root': {
                    width: 32,
                    height: 32,
                    ml: -0.5,
                    mr: 1
                  },
                  '&::before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    right: 14,
                    width: 10,
                    height: 10,
                    bgcolor: 'background.paper',
                    transform: 'translateY(-50%) rotate(45deg)',
                    zIndex: 0
                  }
                }
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              {['Activate', 'De-Activate', 'Delete'].map(option => (
                <MenuItem
                  key={option}
                  disabled={
                    (initialValues.tabValue === 1 && option === 'Activate') ||
                    (initialValues.tabValue === 2 && option === 'De-Activate')
                  }
                  onClick={() => {
                    handleMenuActions(option);
                  }}
                >
                  <ListItemIcon>
                    {option === 'Delete' ? (
                      <>
                        <SvgIcon sx={{ height: 20, width: 20 }}>
                          <TrashIcon color="red" />
                        </SvgIcon>
                      </>
                    ) : option === 'De-Activate' ? (
                      <SvgIcon
                        sx={{ height: 20, width: 20, color: 'orangered' }}
                      >
                        <NotInterested />
                      </SvgIcon>
                    ) : option === 'Activate' ? (
                      <SvgIcon sx={{ height: 20, width: 20, color: 'green' }}>
                        <CheckCircleOutline />
                      </SvgIcon>
                    ) : (
                      ''
                    )}
                  </ListItemIcon>
                  {option}
                </MenuItem>
              ))}
            </Menu>
          </div>
        </Container>
        <Grid sx={{ padding: '18px 0px' }} width={'100%'}>
          {(isRubricsLoading || isActivateDeactivateRubricsLoading) && (
            <Skeleton
              variant="rounded"
              width={'100%'}
              height={400}
              animation="wave"
            />
          )}
          {((!isRubricsLoading && isRubricsSuccess) ||
            (!isActivateDeactivateRubricsLoading &&
              isActivateDeactivateRubricsSuccess)) && (
            <CustomDataTable
              columns={columns}
              allowSelection={true}
              data={
                rubricsData?.rubrics?.length > 0 ? rubricsData?.rubrics : []
              }
              onPageChange={val => {
                setInitialValues({ ...initialValues, page: val });
              }}
              onRowsPerPageChange={val => {
                setInitialValues({ ...initialValues, size: val });
              }}
              selectedRows={[...selectedRubrics]}
              onSelectionChange={val => {
                setSelectedRubrics(val);
              }}
              defaultOrderBy={'creationDate'}
              defaultPage={initialValues.page}
              defaultRowsPerPage={initialValues.size}
              totalCount={rubricsData?.totalCount}
              onRowClick={row => handleOnRowClick(row)}
            />
          )}
        </Grid>

        <ConfirmBox
          isOpen={confirmation}
          title="Delete Rubric!"
          subtitle={`${selectedRubrics.length} rubric[s] are selected, Are you sure you want to delete. `}
          handleClose={() => setConfimation(null)}
          handleSubmit={() => {
            deleteUser();
          }}
        />

        {showError && <SnackBarBox type={'error'} message={showError} />}

        {!isRubricsLoading && isRubricsFailed && (
          <SnackBarBox
            type="error"
            message={handleError(rubricsErrorContainer?.errorMessage)}
          />
        )}

        {!isDeleteRubricsLoading && isDeleteRubricsSuccess && (
          <SnackBarBox
            type="success"
            message={'Rubric[s] deleted successfully. '}
          />
        )}

        {!isDeleteRubricsLoading && isDeleteRubricsFailed && (
          <SnackBarBox
            type="error"
            message={handleError(deleteRubricsErrorContainer?.errorMessage)}
          />
        )}

        {!isActivateDeactivateRubricsLoading &&
          isActivateDeactivateRubricsSuccess && (
            <SnackBarBox
              type="success"
              message={activateDeactivateRubricsResponse}
            />
          )}

        {!isActivateDeactivateRubricsLoading &&
          isActivateDeactivateRubricsFailed && (
            <SnackBarBox
              type="error"
              message={handleError(
                activateDeactivateRubricsErrorContainer?.errorMessage
              )}
            />
          )}
      </Grid>
    </>
  );
}

export default RubricPage;
